// Best-effort device geolocation for the engineer PWA. Used to tag a captured site photo
// with GPS coordinates (evidentiary value for managing agents and housing associations).
//
// This NEVER rejects and NEVER throws: a denied permission, a timeout, an unavailable
// sensor, or a browser without geolocation all resolve to null, so a photo is always
// capturable and the engineer never sees an error for a denied location (expected on some
// devices and networks). The caller stores the photo with no coordinates and proceeds.
//
// Geolocation does not need a network connection (it reads the device GPS), so it works in
// a basement with no signal, which is exactly where the engineers are. It does require a
// secure context (HTTPS), which Render provides.

export interface Coordinates {
  lat: number;
  lng: number;
}

export interface GetPositionOptions {
  timeoutMs?: number;
  // Injectable for testing (defaults to navigator.geolocation).
  geolocation?: Pick<Geolocation, "getCurrentPosition"> | null;
}

const DEFAULT_TIMEOUT_MS = 8000;

// Resolve the device's current coordinates, or null if they cannot be obtained for any
// reason. A short cache (maximumAge) means several photos taken in one visit reuse one fix
// rather than waking the GPS each time.
export function getPositionSafe(opts: GetPositionOptions = {}): Promise<Coordinates | null> {
  const geo =
    opts.geolocation !== undefined
      ? opts.geolocation
      : typeof navigator !== "undefined"
        ? navigator.geolocation
        : null;
  if (!geo || typeof geo.getCurrentPosition !== "function") return Promise.resolve(null);

  const timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  return new Promise<Coordinates | null>((resolve) => {
    let settled = false;
    const finish = (value: Coordinates | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(value);
    };
    // A hard backstop: if the browser never calls back (some engines silently hang on a
    // pending permission prompt), resolve null so the photo flow is never blocked.
    const timer = setTimeout(() => finish(null), timeoutMs);
    try {
      geo.getCurrentPosition(
        (pos) => finish({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => finish(null),
        { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 60_000 },
      );
    } catch {
      finish(null);
    }
  });
}
